import { useContext } from "react";

import { AccountOnHold } from "@/components/AccountOnHold";
import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import { REVIEW_STATUS_APPROVED } from "@/core/constants";
import { theme } from "@/core/theme";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import Stack from "@/tpo/Stack";
import TestStatuses, { getUserTestOrderId } from "@/tpo/TestStatuses";
import { ViewerContext } from "@/tpo/Viewer";

import GenesTestContent from "./GenesTestContent";
import NonGenesTestContent from "./NonGenesTestContent";
import usePatientTestsAndReports from "./usePatientTestsAndReports";

function GenesTest({ userTest }) {
  const { userId } = useContext(ViewerContext);
  return <GenesTestContent clientId={userId} userTest={userTest} />;
}

export default function Tests({ ordersInProgress, userTests, userTest }) {
  const { userId } = useContext(ViewerContext);
  const patientTestsAndReports = usePatientTestsAndReports();
  const displayOrderId = userTest ? getUserTestOrderId(userTest) : null;

  return (
    <>
      <PanelBoxV2
        maxWidth={1538}
        outer={{
          pt: theme.spacing.section.pt,
          px: 20,
          pb: theme.spacing.section.pb
        }}
      >
        <Stack gap={40}>
          <PanelBoxV2 maxWidth={[464, 464, 464, 500]}>
            {!!userTests.length || !!ordersInProgress.length ? (
              <TestStatuses
                heading="Available tests"
                ordersInProgress={ordersInProgress}
                testResults={userTests}
                {...patientTestsAndReports}
                defaultOpen={false}
              />
            ) : (
              <TestStatuses heading="No tests" />
            )}
          </PanelBoxV2>
          {!!userTest && (
            <Center>
              <Box>
                <Box fontFamily="gilroyBold" fontSize={[24, 24, 44]} textAlign="center">
                  {userTest?.name}
                </Box>
                {displayOrderId && (
                  <Box
                    fontFamily="gilroyBold"
                    lineHeight="36px"
                    textAlign="center"
                    fontSize={[18, 18, 24]}
                    mb={20}
                  >
                    {displayOrderId}
                  </Box>
                )}

                <>
                  <Box textAlign="center">
                    {userTest?.earliestDateExtracted ? (
                      <>
                        <Box as="span" fontFamily="gilroyBold">
                          Sample taken:
                        </Box>
                        <Box as="span" fontFamily="gilroyMedium" fontSize={16} ml={1}>
                          {new Date(userTest?.earliestDateExtracted).toLocaleDateString()}
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box as="span" fontFamily="gilroyBold">
                          Results uploaded:
                        </Box>
                        <Box as="span" fontFamily="gilroyMedium" fontSize={16} ml={1}>
                          {new Date(userTest?.created).toLocaleDateString()}
                        </Box>
                      </>
                    )}
                  </Box>
                  <Box textAlign="center">
                    <Box as="span" fontFamily="gilroyBold">
                      Released to client:
                    </Box>
                    <Box as="span" fontFamily="gilroyMedium" fontSize={16} ml={1}>
                      {userTest?.reviewStatus === REVIEW_STATUS_APPROVED ? (
                        <Box as="span" color="green">
                          ✓
                        </Box>
                      ) : (
                        <Box as="span" color="red">
                          ⨯
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              </Box>
            </Center>
          )}
        </Stack>
        <Box py={[15, 15, 20]} />
        {userTest?.resultsBlockedByAccountOnHold ? (
          <AccountOnHold>
            <Box textAlign="center" mx="auto" fontSize={[14, 14, 16]}>
              Your account is currently paused due to an overdue invoice. Contact{" "}
              <ExternalTextLink href="mailto:info@regeneruslabs.com">
                info@regeneruslabs.com
              </ExternalTextLink>{" "}
              to access your results.
            </Box>
          </AccountOnHold>
        ) : (
          !!userTest &&
          (userTest.isGenesTest ? (
            <GenesTest userTest={userTest} />
          ) : (
            <NonGenesTestContent
              userTest={userTest}
              showFullExplanation={false}
              clientId={userId}
            />
          ))
        )}
      </PanelBoxV2>
    </>
  );
}
